/**
 * Which organizations permissions make each navigation entry worth showing.
 *
 * There is no UI-specific permission: every codename below is an existing entry
 * in the organizations permission catalog (`Permission.codename`, see
 * `mbai_organizations/permissions/catalog.py`), and a user's held set arrives on
 * the current-user payload as `effective_permissions`. Navigation visibility is
 * *derived* from that set — the API gates are unchanged and remain the real
 * enforcement; this only stops showing people doors they cannot open.
 *
 * Every entry gates at VIEW level: an entry is shown when the user holds any
 * permission that lets them READ what is behind it. The write controls inside
 * are gated separately, so a viewer lands on the screen read-only rather than
 * being told it does not exist.
 *
 * `RESOURCE_READ_PERMISSIONS` below is the single source for "who can read this
 * resource", and both tables compose their entries out of it. That structure is
 * deliberate: the same resource is reachable from several places (signals from
 * the sidebar's Signal Explorer *and* Configuration → Signals), and hand-listing
 * codenames per entry let those places disagree — the menu hid Signal Explorer
 * from users who could plainly open the signals list one menu over.
 *
 * A section is shown when at least one of its sub-tabs is; a sub-tab with no
 * entry here is always shown (a new tab is visible until it is mapped, rather
 * than silently unreachable).
 */

/**
 * Permissions that grant READ of each resource behind a navigation entry.
 *
 * Derived from the catalog, not guessed. Two catalog rules make write-style
 * permissions grant reads, and both matter here:
 *
 * 1. `_augment_with_read_access` folds a model's `.view` raw into every
 *    write-style OBJECT permission on it — so `edit_line` grants `Machine.view`,
 *    and a role may legitimately grant `edit_line` without `view_line`.
 * 2. Signals and signal description values are subordinate to equipment
 *    (`Signal` -> `MachineSubSystem`), so equipment permissions cascade onto
 *    them — except `share_equipment`, which the catalog deliberately does not
 *    cascade to subordinates.
 *
 * To re-derive any list, ask the catalog which codenames carry the model's
 * `.view` raw:
 *
 *     cd src/mbai_organizations/permissions && python3 -c "
 *     import catalog
 *     t = 'mbai_pm_core.api.models.Signal.view'
 *     print([c for _cat, c, raws in catalog.permission_catalog_with_read_access()
 *            if any(r[0] == t for r in raws)])"
 */
const RESOURCE_READ_PERMISSIONS = {
  // --- Asset hierarchy (CORE) ---
  site: ['view_site', 'add_site', 'edit_site', 'delete_site', 'share_site'],
  area: ['view_area', 'add_area', 'edit_area', 'delete_area', 'share_area'],
  line: ['view_line', 'add_line', 'edit_line', 'delete_line', 'share_line'],
  equipment: [
    'view_equipment',
    'add_equipment',
    'edit_equipment',
    'delete_equipment',
    'share_equipment',
  ],
  // NB no `share_equipment`: it is the one equipment permission that does not
  // cascade to signals.
  signal: ['view_signals', 'view_equipment', 'add_equipment', 'edit_equipment', 'delete_equipment'],
  resourceGroup: ['view_resource_group', 'manage_resource_group_access'],

  // --- Operations surfaces (CORE) ---
  cockpit: ['view_cockpit'],
  dashboard: ['view_dashboard', 'add_dashboard', 'edit_dashboard', 'delete_dashboard'],
  anomaly: ['view_anomaly'],
  comment: ['view_comment', 'add_comment', 'edit_comment', 'delete_comment'],
  threshold: ['view_threshold', 'add_threshold', 'edit_threshold', 'delete_threshold'],
  incident: ['view_incident', 'add_incident', 'edit_incident', 'delete_incident'],
  task: ['view_task', 'add_task', 'edit_task', 'delete_task'],
  report: ['view_report', 'delete_report', 'run_report'],
  batchTracing: ['run_batch_tracing'],
  batchReport: ['view_batch_report', 'run_batch_report'],
  automation: [
    'view_automation',
    'add_automation',
    'edit_automation',
    'delete_automation',
    'run_automation',
  ],
  // FUNCTIONALITY permissions: no structural `.view` counterpart to fold in, so
  // the pair is the whole set.
  configuration: ['view_configuration', 'edit_configuration'],
  digitalTwin: ['view_digital_twin', 'edit_digital_twin'],

  // --- Report authoring (CORE) — templates and letterheads, not the reports ---
  reportTemplate: [
    'add_report_template',
    'edit_report_template',
    'delete_report_template',
    'manage_report_letterheads',
  ],
  batchReportTemplate: [
    'add_batch_report_template',
    'edit_batch_report_template',
    'delete_batch_report_template',
  ],

  // --- Edge ---
  edgeConnection: ['view_edge', 'add_edge', 'edit_edge', 'delete_edge'],

  // --- Alpinist (AICORE) ---
  scheduledPrompt: [
    'view_alpinist_scheduled_prompt',
    'add_alpinist_scheduled_prompt',
    'edit_alpinist_scheduled_prompt',
    'delete_alpinist_scheduled_prompt',
    'run_alpinist_scheduled_prompt',
  ],
  kbDocument: ['view_kb_document', 'add_kb_document', 'edit_kb_document', 'delete_kb_document'],
  alpinistIntegration: [
    'view_alpinist_integration',
    'add_alpinist_integration',
    'edit_alpinist_integration',
    'delete_alpinist_integration',
  ],
  alpinistChat: ['use_alpinist_chat'],

  // --- Account administration (ORGANIZATION) ---
  // These categories are administrative gates rather than resources and are kept
  // out of the ordinary member grant, so `view_*` here already means "is an
  // administrator of this".
  user: ['view_users', 'add_users', 'edit_users', 'delete_users'],
  team: ['view_teams', 'add_teams', 'edit_teams', 'delete_teams'],
  role: ['view_roles', 'add_roles', 'edit_roles', 'delete_roles'],
  notificationSubscription: [
    'view_notification_subscriptions',
    'manage_notification_subscriptions',
  ],
} as const satisfies Record<string, readonly string[]>

const union = (...sets: readonly (readonly string[])[]): readonly string[] => [
  ...new Set(sets.flat()),
]

/** Sidebar entries, keyed by the `id` each one carries in `Drawer.tsx`. */
export const SIDEBAR_PERMISSIONS: Record<string, readonly string[]> = {
  'mbai-main-line-overview-link': RESOURCE_READ_PERMISSIONS.line,
  'mbai-main-cockpit-link': RESOURCE_READ_PERMISSIONS.cockpit,
  'mbai-main-custom-dashboards-link': RESOURCE_READ_PERMISSIONS.dashboard,
  // Batch traceability runs traces and reads the batch reports produced from them.
  'mbai-main-batch-traceability-link': union(
    RESOURCE_READ_PERMISSIONS.batchTracing,
    RESOURCE_READ_PERMISSIONS.batchReport,
  ),
  'mbai-main-signals-link': RESOURCE_READ_PERMISSIONS.signal,
  'mbai-main-anomalies-link': RESOURCE_READ_PERMISSIONS.anomaly,
  'mbai-main-logbook-link': RESOURCE_READ_PERMISSIONS.comment,
  'mbai-main-thresholds-link': RESOURCE_READ_PERMISSIONS.threshold,
  // Signal value trackers are signal-derived and have no catalog entry of their
  // own, so they follow signal read.
  'mbai-main-signal-value-trackers-link': RESOURCE_READ_PERMISSIONS.signal,
  'mbai-main-incidents-link': RESOURCE_READ_PERMISSIONS.incident,
  'mbai-main-reports-link': RESOURCE_READ_PERMISSIONS.report,
  'mbai-main-tasks-link': RESOURCE_READ_PERMISSIONS.task,
  // Home and Settings are deliberately absent: Home is the landing route, and
  // Settings always holds the Personal section (profile, notifications), which
  // every user may reach. External bookmarks carry their own `hasExternalBookmarks`
  // gate.
}

/**
 * Settings sub-tabs, keyed by the `id` each one carries in `SubMenu.tsx`, which
 * is also the `?tab=` value the section components switch on.
 */
export const SETTINGS_TAB_PERMISSIONS: Record<string, readonly string[]> = {
  // --- Configuration (`sub_menu=machine_admin`) ---
  production_sites: RESOURCE_READ_PERMISSIONS.site,
  production_areas: RESOURCE_READ_PERMISSIONS.area,
  line_management: RESOURCE_READ_PERMISSIONS.line,
  equipment_management: RESOURCE_READ_PERMISSIONS.equipment,
  // Equipment types classify equipment and have no catalog entry of their own.
  equipment_types: RESOURCE_READ_PERMISSIONS.equipment,
  // EquipmentGroup has no catalog entry of its own yet either -- piggybacks on
  // equipment's read permission set, same as equipment_types above.
  equipment_groups: RESOURCE_READ_PERMISSIONS.equipment,
  // The same `signal` set the sidebar's Signal Explorer uses — these two screens
  // list the same objects, so a permission that reveals one must reveal both.
  signals: RESOURCE_READ_PERMISSIONS.signal,
  signal_types: RESOURCE_READ_PERMISSIONS.signal,
  resource_groups: RESOURCE_READ_PERMISSIONS.resourceGroup,
  // Programs/triggers/detectors are exposed through the CONFIGURATION category.
  program_classifications: RESOURCE_READ_PERMISSIONS.configuration,
  // Managing the logbook's categories is its own catalog permission.
  logbook_categories: ['manage_comment_categories'],
  digital_twin: RESOURCE_READ_PERMISSIONS.digitalTwin,
  thresholds: RESOURCE_READ_PERMISSIONS.threshold,
  // A configuration surface covering the reports themselves, their templates and
  // the letterheads, plus the batch-report templates that share the tab.
  reporting: union(
    RESOURCE_READ_PERMISSIONS.report,
    RESOURCE_READ_PERMISSIONS.reportTemplate,
    RESOURCE_READ_PERMISSIONS.batchReport,
    RESOURCE_READ_PERMISSIONS.batchReportTemplate,
  ),
  automations: RESOURCE_READ_PERMISSIONS.automation,
  connections: RESOURCE_READ_PERMISSIONS.edgeConnection,

  // --- Account Management ---
  // Business administration rather than plant configuration, and deliberately
  // left as it was: the category-level mapping matches the existing
  // `can_manage_users` / `can_manage_teams` / `can_manage_roles` flags.
  users: RESOURCE_READ_PERMISSIONS.user,
  // Who may join the organization from its email domains. Further limited to
  // organization owners/admins and staff (account/useCanManageOrganizationDomains),
  // as the user permissions are granted broadly.
  domains: RESOURCE_READ_PERMISSIONS.user,
  teams: RESOURCE_READ_PERMISSIONS.team,
  roles: RESOURCE_READ_PERMISSIONS.role,
  notification_subscriptions: RESOURCE_READ_PERMISSIONS.notificationSubscription,
  // Shop-floor screens (AB#6756): part of administering the organization, so
  // the user permissions as for the section, and further limited to its
  // owners/admins and staff like Domains. A dashboard's editors manage its
  // screens from its Share dialog.
  display_screens: RESOURCE_READ_PERMISSIONS.user,
  // Seat/signal usage is part of administering the organization's users. The
  // tab is further limited to organization owners/admins and staff
  // (account/useCanViewOrganizationUsage), as the user permissions are granted
  // broadly.
  usage: RESOURCE_READ_PERMISSIONS.user,

  // --- Alpinist ---
  alpinist_scheduled_prompts: RESOURCE_READ_PERMISSIONS.scheduledPrompt,
  alpinist_knowledge_base: RESOURCE_READ_PERMISSIONS.kbDocument,
  alpinist_integrations: RESOURCE_READ_PERMISSIONS.alpinistIntegration,
  // Memory, Skills and Management have no catalog permission of their own. They
  // administer the assistant itself, so they follow the chat capability rather
  // than being mapped to an unrelated codename; the section's AI read
  // capability (`serviceCapabilities.ai`) still gates their content.
  alpinist_memory: RESOURCE_READ_PERMISSIONS.alpinistChat,
  alpinist_skills: RESOURCE_READ_PERMISSIONS.alpinistChat,
  alpinist_mgmt: RESOURCE_READ_PERMISSIONS.alpinistChat,

  // Personal (`user_profile`, `notifications`) and Organizations & Billing
  // (`billing_accounts`, `subscription_management`, `organizations`) are
  // deliberately unmapped. Personal is every user's own profile. Organizations &
  // Billing is business administration and stays out of this change; its access
  // is relation-based in organizations (billing-account owner/admin,
  // subscription admin) and not carried by the permission catalog at all, so
  // that section keeps its existing `isSubscriptionAdmin` gate.
}
